import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Spin, Upload } from "antd";
import { useEffect, useMemo, useState } from "react";
import { MdDelete } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../../api";
import { MAX_FILE_SIZE } from "../../../config/Constant";
import { useAdminSettingContext } from "../../../context/AdminSettingContext";
import { getFileIcon } from "../../../utils/SendFile";
import { openNotificationError } from "../../../utils/Utils";
import { GeneralModal } from "../GeneralModal";
import { PreviewUploadModal } from "./PreviewUploadModal";

const StoreVersionModal = ({
  isModalOpen,
  cancelModal,
  title,
  languageMap,
  selectedVersion,
  setVersionList,
  versionList,
  selectedApplication,
}) => {
  const { selectedApp } = useAdminSettingContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenUploadModal, setIsOpenUploadModal] = useState(false);
  const [previewFile, setPreviewFile] = useState();
  const [dataForm, setDataForm] = useState({});
  const [uploadFile, setUploadFile] = useState(null);
  const [appList, setAppList] = useState([]);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    setIsLoading(true);

    try {
      const request = {
        ...values,
        appId: selectedApp?.appId,
        file: dataForm,
        versionId: selectedVersion?.versionId,
      };

      let result = await apiFactory.versionApi.store(request);

      if (selectedVersion?.appId) {
        const appIndex = versionList?.findIndex(
          (app) => app?.appId === selectedVersion?.appId
        );

        versionList.splice(appIndex, 1);
      }

      versionList?.unshift({
        ...result?.data,
        appName: selectedApp?.name,
        type: selectedApp?.type,
      });
      setVersionList(...[versionList]);
      cancelModal();
    } catch (error) {
      console.error("Error fetching alarm list data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelUpload = () => {
    setIsOpenUploadModal(false);
    setUploadFile();
  };

  const handleDelete = () => {
    setUploadFile(null);
  };

  const handleChange = async (event) => {
    const status = event?.file?.status;

    if (status === "removed") {
      handleDelete(null);
      return;
    }

    if (status === "uploading") {
      setIsOpenUploadModal(true);

      const file = event?.file;
      const originFile = file?.originFileObj;

      if (
        originFile?.webkitRelativePath &&
        originFile.webkitRelativePath.includes("/")
      ) {
        openNotificationError(
          languageMap?.["error.not.sendFolder"] ?? "You can not send folder"
        );
        setIsOpenUploadModal(false);
        return;
      }

      if (file?.size > MAX_FILE_SIZE) {
        openNotificationError("File size exceeds the 1gb limit");
        setIsOpenUploadModal(false);
        return;
      }

      originFile.uid = uuidv4();
      setUploadFile(originFile);
    }
  };

  const fetchAppList = async () => {
    setIsLoading(true);
    try {
      const result = await apiFactory.appApi.getList();

      if (result?.status === 200) {
        setAppList(
          result?.data?.map((app) => ({
            value: app?.appId,
            label: app?.name,
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching file list data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onDelete = async () => {
    if (!selectedVersion?.versionId) return;

    let result = await apiFactory.versionApi.delete({
      versionId: selectedVersion?.versionId,
    });

    if (result?.status === 200) {
      const versionIndex = versionList?.findIndex(
        (version) => version?.versionId === selectedVersion?.versionId
      );

      versionList.splice(versionIndex, 1);
      setVersionList(...[versionList]);

      cancelModal();
    }
  };

  const chooseFile = useMemo(() => {
    if (uploadFile?.name) {
      return (
        <Upload
          listType="picture-card"
          fileList={[uploadFile]}
          onChange={handleChange}
          itemRender={(originNode, f) => {
            return (
              <div className="custom-thumbnail">
                <div className="file-thumbnail">
                  {getFileIcon(f.name)}
                  <span>
                    {f?.name?.length > 10
                      ? f?.name?.substring(0, 10) + "..."
                      : f?.name}
                  </span>
                  <button className="delete-button" onClick={handleDelete}>
                    <MdDelete color="red" size={20} />
                  </button>
                </div>
              </div>
            );
          }}
        />
      );
    }

    return (
      <Upload listType="picture-card" fileList={[]} onChange={handleChange}>
        <div className="flex items-center justify-center">
          <div className="flex flex-col items-center">
            <PlusOutlined />
            <span className="mt-1">Upload</span>
          </div>
        </div>
      </Upload>
    );
  }, [uploadFile]);

  useEffect(() => {
    fetchAppList();
  }, []);

  useEffect(() => {
    setUploadFile({
      name: selectedVersion?.file?.originalName,
    });
    setDataForm({ ...selectedVersion?.file });
  }, [selectedVersion?.file]);

  return (
    <Modal
      open={isModalOpen}
      footer={false}
      closeIcon={false}
      onCancel={cancelModal}
      //   title={languageMap?.["modal.labelManagement.title"] ?? "Label management"}
      title={title}
      closable={true}
      maskClosable={false}
    >
      <div>
        <Form
          onFinish={onFinish}
          initialValues={selectedVersion}
          autoComplete="off"
          layout="vertical"
          form={form}
          // onKeyDown={preventSubmitOnEnter}
        >
          <Form.Item
            label={"Name"}
            name="name"
            rules={[
              {
                required: true,
                message:
                  languageMap?.["as.menu.organization.organizationName"] ??
                  "Please input name!",
              },
            ]}
            normalize={(value) => (value ? value.toUpperCase() : "")}
          >
            <Input type="text" className="w-[100%]" maxLength={50} />
          </Form.Item>
          <Form.Item
            label="Order"
            name="order"
            rules={[
              {
                required: true,
                message:
                  languageMap?.["as.menu.organization.organizationName"] ??
                  "Please input name!",
              },
            ]}
          >
            <Input
              type="text"
              className="w-[100%]"
              maxLength={5}
              onKeyPress={(event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
            />
          </Form.Item>

          <Form.Item label={"Other link"} name="otherLink">
            <Input type="text" className="w-[100%]" maxLength={50} />
          </Form.Item>
          <Form.Item label="File" name="url">
            {chooseFile}
          </Form.Item>

          {isLoading ? (
            <div className="flex justify-center mt-[10px]">
              <Spin
                indicator={<LoadingOutlined className="loader-icon" spin />}
              />
            </div>
          ) : (
            <div className="flex gap-[10px] justify-center mt-[10px]">
              <Button
                type="primary"
                htmlType="submit"
                className="bg-[grey]"
                onClick={cancelModal}
              >
                {languageMap?.["as.menu.organization.btnCancel"] ?? "Cancel"}
              </Button>
              <Button type="primary" className="bg-[#4db74d]" htmlType="submit">
                {selectedVersion
                  ? languageMap?.["as.menu.app.btnUpdate"] ?? "Update"
                  : languageMap?.["as.menu.app.btnCreate"] ?? "Create"}
              </Button>
              {selectedVersion && (
                <Button
                  type="primary"
                  className="bg-[red]"
                  onClick={() => setIsDeleteModal(true)}
                >
                  {languageMap?.["as.menu.organization.btnCancel"] ?? "Delete"}
                </Button>
              )}
            </div>
          )}

          {isOpenUploadModal && (
            <PreviewUploadModal
              isModalOpen={isOpenUploadModal}
              handleCancel={handleCancelUpload}
              setIsOpenUploadModal={setIsOpenUploadModal}
              setDataForm={setDataForm}
              uploadFile={uploadFile}
              setUploadFile={setUploadFile}
              setPreviewFile={setPreviewFile}
            />
          )}

          {isDeleteModal && (
            <GeneralModal
              open={isDeleteModal}
              onCancel={() => setIsDeleteModal(false)}
              onConfirm={onDelete}
            />
          )}
        </Form>
      </div>
    </Modal>
  );
};
export { StoreVersionModal };
